/**
 * Keeps `--announcement-bar-height` in sync with the real height of the fixed
 * announcement bar, so the page offset and the sticky header offset stay correct
 * when the copy wraps, the viewport changes, or the merchant edits the bar in the
 * theme editor.
 *
 * The section also renders a Liquid-computed fallback for this variable, so the
 * layout is already correct on first paint and this script only refines it.
 */

const HEIGHT_VARIABLE = '--announcement-bar-height';
const BAR_SELECTOR = '[data-announcement-bar-fixed]';

/** @type {ResizeObserver | undefined} */
let resizeObserver;

/**
 * Writes the measured bar height to the document root.
 * @param {number} height - Height in CSS pixels.
 */
function setHeight(height) {
  if (!Number.isFinite(height) || height <= 0) return;

  document.documentElement.style.setProperty(HEIGHT_VARIABLE, `${Math.round(height)}px`);
}

/**
 * Finds the fixed bar (if any) and keeps its height measured.
 * Safe to call repeatedly — it always tears down the previous observer first.
 */
function measure() {
  try {
    resizeObserver?.disconnect();
    resizeObserver = undefined;

    const bar = document.querySelector(BAR_SELECTOR);

    if (!(bar instanceof HTMLElement)) {
      // No fixed bar on this page: hand control back to the stylesheet fallback.
      document.documentElement.style.removeProperty(HEIGHT_VARIABLE);
      return;
    }

    setHeight(bar.getBoundingClientRect().height);

    if (typeof ResizeObserver === 'undefined') return;

    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHeight(entry.target.getBoundingClientRect().height);
      }
    });

    resizeObserver.observe(bar);
  } catch (error) {
    // Never let a measurement failure break the page — the Liquid fallback still applies.
    console.warn('[announcement-bar-fixed] could not measure the announcement bar', error);
  }
}

/** Re-measures on the next frame, after the DOM has settled. */
function scheduleMeasure() {
  requestAnimationFrame(measure);
}

measure();

// The theme editor swaps section markup in and out without a page load.
document.addEventListener('shopify:section:load', scheduleMeasure);
document.addEventListener('shopify:section:unload', scheduleMeasure);
document.addEventListener('shopify:block:select', scheduleMeasure);
window.addEventListener('pageshow', scheduleMeasure);
